import { PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import SimpleCombobox from "../../../../components/SimpleCombobox";
import { useEffect, useState } from "react";
import api from "../../../../api";

export const ScheduleForm = ({
    dialogRef,
    schedule: initialSchedule,
    onSave,
    notify,
}) => {
    const [doctors, setDoctors] = useState([]);
    const [schedule, setSchedule] = useState({
        doctor: null,
        duration: 0,
        slots: [
            {
                dayOfWeek: "",
                timeRanges: [{ startTime: "", endTime: "" }],
            },
        ],
    });

    useEffect(() => {
        if (initialSchedule?.id) {
            setSchedule({
                id: initialSchedule.id,
                doctor: {
                    ...initialSchedule.doctor,
                    name: `${initialSchedule.doctor.firstName} ${initialSchedule.doctor.lastName} ${initialSchedule.doctor.govId}`,
                },
                duration: initialSchedule.duration || 0,
                slots: initialSchedule.slots || [],
            });
        }
    }, [initialSchedule]);

    const scheduleChange = (field, value) => {
        setSchedule((prev) => ({
            ...prev,
            [field]: value,
        }));
    };
    useEffect(() => {
        api.doctors.getMulti().then((response) => {
            const data = response;
            const doctorsOptions = data.map((doc) => ({
                id: doc.id,
                name: `${doc.firstName} ${doc.lastName} ${doc.govId}`,
            }));
            setDoctors(doctorsOptions);
        });
    }, []);

    const handleSlotDayChange = (slotIndex, day) => {
        setSchedule((prev) => ({
            ...prev,
            slots: prev.slots.map((s, i) =>
                i === slotIndex ? { ...s, dayOfWeek: day } : s
            ),
        }));
    };

    const handleTimeChange = (slotIndex, trIndex, field, value) => {
        setSchedule((prev) => ({
            ...prev,
            slots: prev.slots.map((s, i) =>
                i === slotIndex
                    ? {
                          ...s,
                          timeRanges: s.timeRanges.map((tr, j) =>
                              j === trIndex ? { ...tr, [field]: value } : tr
                          ),
                      }
                    : s
            ),
        }));
    };

    const addTimeRange = (slotIndex) => {
        setSchedule((prev) => ({
            ...prev,
            slots: prev.slots.map((s, i) =>
                i === slotIndex
                    ? {
                          ...s,
                          timeRanges: [
                              ...s.timeRanges,
                              { startTime: "", endTime: "" },
                          ],
                      }
                    : s
            ),
        }));
    };

    const removeTimeRange = (slotIndex, trIndex) => {
        setSchedule((prev) => {
            const slot = prev.slots[slotIndex];
            if (!slot || slot.timeRanges.length <= 1) return prev;
            return {
                ...prev,
                slots: prev.slots.map((s, i) =>
                    i === slotIndex
                        ? {
                              ...s,
                              timeRanges: s.timeRanges.filter(
                                  (_, j) => j !== trIndex
                              ),
                          }
                        : s
                ),
            };
        });
    };

    const addSlot = () => {
        setSchedule((prev) => ({
            ...prev,
            slots: [
                ...prev.slots,
                { dayOfWeek: "", timeRanges: [{ startTime: "", endTime: "" }] },
            ],
        }));
    };

    const removeSlot = (slotIndex) => {
        setSchedule((prev) => {
            if (prev.slots.length === 1) return prev;

            return {
                ...prev,
                slots: prev.slots.filter((_, i) => i !== slotIndex),
            };
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const { isValid, errors } = validateSchedule();
        if (!isValid) {
            alert(errors.join("\n"));
            return;
        }

        const payload = {
            ...schedule,
            doctor: schedule.doctor?.id || null,
        };

        try {
            if (schedule.id) {
                await api.schedules.update(schedule.id, payload);
                notify("Agenda actualizada correctamente", "success");
            } else {
                await api.schedules.create(payload);
                notify("Agenda creada correctamente", "success");
            }
            onSave?.();
            dialogRef.current?.close();
        } catch (error) {
            console.error("Error al guardar la agenda:", error);
            if (error?.message && typeof error.message === "object") {
                const messages = Object.values(error.message).flat().join("\n");
                notify(messages, "error");
            } else {
                notify(
                    "Hubo un error al guardar la agenda. Inténtalo nuevamente",
                    "error"
                );
            }
        }
    };

    const validateSchedule = () => {
        const errors = [];

        if (!schedule.doctor) errors.push("Debés seleccionar un doctor.");
        if (!schedule.duration || schedule.duration <= 0)
            errors.push("La duración debe ser mayor a 0.");

        if (!schedule.slots || schedule.slots.length === 0) {
            errors.push("Debés agregar al menos un día con horarios válidos.");
        } else {
            schedule.slots.forEach((slot, i) => {
                if (!slot.dayOfWeek)
                    errors.push(
                        `El día del slot de horario ${i + 1} es obligatorio.`
                    );

                if (!slot.timeRanges || slot.timeRanges.length === 0) {
                    errors.push(
                        `El slot ${
                            i + 1
                        } debe tener al menos un rango de tiempo.`
                    );
                } else {
                    slot.timeRanges.forEach((tr, j) => {
                        if (!tr.startTime || !tr.endTime) {
                            errors.push(
                                `El rango horario ${j + 1} del slot ${
                                    i + 1
                                } debe tener hora de inicio y fin.`
                            );
                        } else if (tr.startTime >= tr.endTime) {
                            errors.push(
                                `En el rango horario ${j + 1} del slot ${
                                    i + 1
                                }, la hora de inicio debe ser menor que la hora de fin.`
                            );
                        }
                    });
                }
            });
        }
        return {
            isValid: errors.length === 0,
            errors,
        };
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-900 text-black dark:text-white">
            <div className="text-white bg-[#8098F9] dark:bg-[#4f5ea5] p-5">
                <h2 className="text-3xl font-bold">Nuevo horario</h2>
                <small className="text-[#C7D7FE]">
                    Empezá completando los datos a continuación para agendar tu
                    consulta médica.
                </small>
            </div>
            <form
                onSubmit={handleSubmit}
                className=" text-gray-800 dark:text-gray-200 flex flex-col justify-between h-full"
            >
                <div className="p-5 flex flex-col gap-5">
                    <div className="flex flex-col gap-2">
                        <SimpleCombobox
                            inputClassName="relative mt-1"
                            placeholder={"Buscar"}
                            handleChange={scheduleChange}
                            name="doctor"
                            label="Doctor"
                            labelClassname="font-semibold"
                            options={doctors}
                            required={true}
                            value={schedule.doctor}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <div>
                            <label htmlFor="duration" className="font-semibold">
                                Duración de la cita (minutos)
                            </label>
                            <span className="font-medium text-[#F51515] inline-block ml-2">
                                *
                            </span>
                        </div>
                        <input
                            onChange={(e) =>
                                scheduleChange("duration", e.target.value)
                            }
                            value={schedule.duration}
                            type="number"
                            name="duration"
                            id="duration"
                            placeholder="10 min"
                            className="input-base"
                            required
                            step={5}
                        />
                    </div>
                    <div className="flex justify-between items-center">
                        <div className="flex flex-col">
                            <span className="text-lg font-semibold">
                                Días y horarios disponibles
                            </span>
                            <small className="text-[#98A2B3]">
                                Selecciona los días y configura horarios
                                específicos para cada uno
                            </small>
                        </div>
                        <button
                            type="button"
                            className="bg-[#465FFF] text-white p-1 rounded-lg"
                            onClick={addSlot}
                        >
                            <PlusIcon className="h-7 w-7" />
                        </button>
                    </div>
                    {schedule.slots.map((slot, slotIndex) => (
                        <div className="bg-[#F2F4F7] dark:bg-gray-800 flex flex-col p-3 gap-2 rounded-xl">
                            <div className="w-full flex justify-end">
                                <button
                                    type="button"
                                    className="text-[#C7D7FE]"
                                    onClick={() => removeSlot(slotIndex)}
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="flex flex-col ">
                                <label htmlFor="day" className="font-semibold">
                                    Día
                                </label>
                                <select
                                    name="day"
                                    id="day"
                                    className="input-base"
                                    value={slot.dayOfWeek}
                                    onChange={(e) =>
                                        handleSlotDayChange(
                                            slotIndex,
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="" disabled selected hidden>
                                        Seleccione un dia
                                    </option>
                                    <option value="1">Lunes</option>
                                    <option value="2">Martes</option>
                                    <option value="3">Miércoles</option>
                                    <option value="4">Jueves</option>
                                    <option value="5">Viernes</option>
                                    <option value="6">Sábado</option>
                                    <option value="7">Domingo</option>
                                </select>
                            </div>
                            {slot.timeRanges.map((tr, trIndex) => (
                                <div className="flex justify-between items-center gap-10">
                                    <div className="flex flex-col flex-1">
                                        <label
                                            htmlFor="startTime"
                                            className="font-semibold"
                                        >
                                            Desde
                                        </label>
                                        <input
                                            type="time"
                                            name="startTime"
                                            id="startTime"
                                            className="input-base"
                                            value={tr.startTime}
                                            onChange={(e) =>
                                                handleTimeChange(
                                                    slotIndex,
                                                    trIndex,
                                                    "startTime",
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="flex flex-col flex-1">
                                        <label
                                            htmlFor="endTime"
                                            className="font-semibold"
                                        >
                                            Hasta
                                        </label>
                                        <input
                                            type="time"
                                            name="endTime"
                                            id="endTime"
                                            className="input-base"
                                            value={tr.endTime}
                                            onChange={(e) =>
                                                handleTimeChange(
                                                    slotIndex,
                                                    trIndex,
                                                    "endTime",
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="flex justify-between gap-2 items-center ">
                                        {trIndex <
                                            slot.timeRanges.length - 1 && (
                                            <button
                                                type="button"
                                                className="bg-white dark:bg-gray-900 p-1 rounded-lg"
                                                onClick={() =>
                                                    removeTimeRange(
                                                        slotIndex,
                                                        trIndex
                                                    )
                                                }
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        )}

                                        {trIndex ===
                                            slot.timeRanges.length - 1 && (
                                            <button
                                                type="button"
                                                className="bg-white dark:bg-gray-900 p-1 rounded-lg"
                                                onClick={() =>
                                                    addTimeRange(slotIndex)
                                                }
                                            >
                                                <PlusIcon className="h-5 w-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div className="flex justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
                    <button
                        type="button"
                        className="text-gray-500"
                        onClick={() => dialogRef.current?.close()}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="bg-[#465FFF] text-white px-4 py-2 rounded-lg"
                    >
                        Guardar
                    </button>
                </div>
            </form>
        </div>
    );
};
